import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box, Button, CircularProgress,
  Paper, Step, StepLabel, Stepper, Typography,
} from "@mui/material";
import ArrowBackIcon    from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckIcon        from "@mui/icons-material/Check";
import { toast } from "react-toastify";
import api from "../../config/axios";

import PatientInfo   from "../AddPatient/sections/PatientInfo";
import PremorbidInfo from "../AddPatient/sections/PremorbidInfo";
import Diagnosis     from "../AddPatient/sections/Diagnosis";
import BaselineData  from "../AddPatient/sections/BaselineData";
import RgsConfig     from "../AddPatient/sections/RgsConfig";

const STEPS = [
  "Información del paciente",
  "Información premórbida",
  "Diagnóstico",
  "Datos basales",
  "Cuenta RGS+",
];

const bool = (v) => (v === null || v === undefined ? null : Boolean(v));

const parseArr = (v) => {
  if (Array.isArray(v)) return v;
  if (typeof v === "string") { try { return JSON.parse(v); } catch { return []; } }
  return [];
};

function toFormShape(p) {
  const dateStr = (() => {
    if (!p.date_of_birth) return "";
    const d = p.date_of_birth instanceof Date ? p.date_of_birth : new Date(p.date_of_birth);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  })();

  return {
    patient_code:   p.patient_code   || "",
    gender:         p.gender         || "",
    date_of_birth:  dateStr,
    weight_kg:      p.weight_kg      ?? "",
    education:      p.education      || "",
    marital_status: p.marital_status || "",
    dominant_hand:  p.dominant_hand  || "",
    occupation:     p.occupation     || "",

    vascular_risk_factors:          parseArr(p.vascular_risk_factors),
    locomotor_problems:             bool(p.locomotor_problems),
    previous_events:                parseArr(p.previous_events),
    comorbidities:                  parseArr(p.comorbidities),
    medical_resources:              p.medical_resources              || "",
    physical_condition_days_week:   p.physical_condition_days_week   ?? "",
    physical_condition_sport_types: p.physical_condition_sport_types || "",
    independence_status:            p.independence_status            ?? "",
    walking_status:                 p.walking_status                 || "",
    falls_more_than_2:              bool(p.falls_more_than_2),
    falls_description:              p.falls_description              || "",

    time_since_stroke_months: p.time_since_stroke_months ?? "",
    stroke_type:              p.stroke_type              || "",
    stroke_location:          parseArr(p.stroke_location),
    stroke_location_detail:   parseArr(p.stroke_location_detail),
    nihss:                    p.nihss                    ?? "",
    stroke_status:            p.stroke_status            || "",
    affected_side:            p.affected_side            || "",
    neglect_present:          bool(p.neglect_present),
    neglect_side:             p.neglect_side             || "",
    visual_impairment_present: p.visual_impairment_present || "",
    visual_impairment_type:   parseArr(p.visual_impairment_type),
    moca:                     p.moca                     ?? "",
    depression_anxiety:       p.depression_anxiety       || "",

    nutritional_info:               p.nutritional_info               || "",
    autonomous_system:              p.autonomous_system              || "",
    grip_strength_affected_arm:     p.grip_strength_affected_arm     ?? "",
    grip_strength_unaffected_arm:   p.grip_strength_unaffected_arm   ?? "",
    grip_robot_measured:            bool(p.grip_robot_measured),
    arat:                           p.arat                           ?? "",
    fugl_meyer_ue:                  p.fugl_meyer_ue                  ?? "",

    PATIENT_USER:             p.PATIENT_USER             || "",
    PASSWORD:                 "",
    EMAIL:                    p.EMAIL                    || "",
    PARETIC_SIDE:             p.PARETIC_SIDE             || "",
    UPPER_EXTREMITY_TO_TRAIN: p.UPPER_EXTREMITY_TO_TRAIN || "",
    HAND_RAISING_CAPACITY:    p.HAND_RAISING_CAPACITY    || "",
  };
}

export default function EditPatient() {
  const { id }      = useParams();
  const navigate    = useNavigate();
  const [step,      setStep]      = useState(0);
  const [form,      setForm]      = useState(null);
  const [fetching,  setFetching]  = useState(true);
  const [loading,   setLoading]   = useState(false);
  useEffect(() => {
    api.get(`/patients/${id}`)
      .then(({ data }) => setForm(toFormShape(data)))
      .catch(() => {
        toast.error("Error al cargar los datos del paciente.");
        navigate("/patients");
      })
      .finally(() => setFetching(false));
  }, [id, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleNext = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const handleBack = () => setStep((s) => Math.max(s - 1, 0));

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const payload = { ...form };

      const numericFields = [
        "weight_kg", "physical_condition_days_week", "independence_status",
        "time_since_stroke_months", "nihss", "moca",
        "grip_strength_affected_arm", "grip_strength_unaffected_arm",
        "arat", "fugl_meyer_ue",
      ];
      numericFields.forEach((f) => {
        if (payload[f] === "") payload[f] = null;
        else if (payload[f] !== null) payload[f] = Number(payload[f]);
      });

      ["PATIENT_USER", "PASSWORD", "EMAIL", "PARETIC_SIDE", "UPPER_EXTREMITY_TO_TRAIN", "HAND_RAISING_CAPACITY"].forEach((f) => {
        if (payload[f] === "") payload[f] = null;
      });

      await api.put(`/patients/${id}`, payload);
      toast.success("Paciente actualizado correctamente.");
      navigate("/patients");
    } catch (err) {
      const msg = err.response?.data?.error || "Error al guardar los cambios.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  const sectionProps = { form, onChange: handleChange, onCheckboxChange: handleCheckboxChange };

  const sections = [
    <PatientInfo   {...sectionProps} />,
    <PremorbidInfo {...sectionProps} />,
    <Diagnosis     {...sectionProps} />,
    <BaselineData  {...sectionProps} />,
    <RgsConfig     {...sectionProps} />,
  ];

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
        <Button
          variant="text"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/patients")}
          sx={{ minWidth: 0 }}
        >
          Volver
        </Button>
        <Typography variant="h5" fontWeight={700}>
          Editar paciente
        </Typography>
      </Box>

      <Stepper activeStep={step} sx={{ mb: 4 }} alternativeLabel>
        {STEPS.map((label, index) => (
          <Step
            key={label}
            onClick={() => setStep(index)}
            sx={{ cursor: "pointer", "& *": { cursor: "pointer" } }}
          >
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Paper sx={{ p: { xs: 2.5, sm: 4 }, borderRadius: 2, boxShadow: 2, mb: 3 }}>
        {sections[step]}
      </Paper>

      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          disabled={step === 0}
        >
          Anterior
        </Button>

        {step < STEPS.length - 1 ? (
          <Button
            variant="contained"
            endIcon={<ArrowForwardIcon />}
            onClick={handleNext}
          >
            Siguiente
          </Button>
        ) : (
          <Button
            variant="contained"
            color="success"
            startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <CheckIcon />}
            onClick={handleSubmit}
            disabled={loading}
            sx={{ fontWeight: 700 }}
          >
            {loading ? "Guardando..." : "Guardar cambios"}
          </Button>
        )}
      </Box>
    </Box>
  );
}
